import React from 'react'
import { Box, Container, Typography, IconButton } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { useTranslations } from 'next-intl'
import { FlightOffer } from '@/src/types/flightOffer'

interface HomepageCarouselProps {
    title: string
    offers: FlightOffer[]
    currentIndex: number
    onPrev: () => void
    onNext: () => void
    onMouseEnter: () => void
    onMouseLeave: () => void
    onOfferClick: (slug: string | undefined) => void
}

export const HomepageCarousel: React.FC<HomepageCarouselProps> = ({
    title,
    offers,
    currentIndex,
    onPrev,
    onNext,
    onMouseEnter,
    onMouseLeave,
    onOfferClick
}) => {
    const t = useTranslations('homePage.headings')

    if (!offers || offers.length === 0) {
        return null
    }

    // Get 3 cards starting from currentIndex
    const getVisibleCards = () => {
        const visibleCards = []
        for (let i = 0; i < 3; i++) {
            const index = (currentIndex + i) % offers.length
            visibleCards.push(offers[index])
        }
        return visibleCards
    }

    const visibleCards = getVisibleCards()

    return (
          <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
            <Box
                sx={{
                    position: 'relative',
                    mx: 'auto',
                    maxWidth: { xs: '100%', md: '1200px' }
                }}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {/* Navigation Arrows */}
                <IconButton
                    onClick={onPrev}
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        position: 'absolute',
                        left: -60,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 20,
                        backgroundColor: 'white',
                        width: 48,
                        height: 48
                    }}
                >
                    <ChevronLeft fontSize='large' />
                </IconButton>

                <Box
                    sx={{
                        display: 'flex',
                        gap: { xs: 0, md: 3 },
                        justifyContent: { xs: 'flex-start', md: 'space-between' },
                        alignItems: 'stretch',
                        transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                        overflow: { xs: 'visible', md: 'hidden' },
                        px: { xs: 2, md: 0 }
                    }}
                >
                    {visibleCards.map((offer, index) => (
                        <Box
                            key={`${offer.offerId}-${currentIndex}-${index}`}
                            sx={{
                                display: { xs: index === 0 ? 'block' : 'none', md: 'block' },
                                flex: { md: '1 1 0' },
                                minWidth: 0,
                                width: { xs: '100%', md: 'auto' }
                            }}
                        >
                            <BannerCard
                                offer={offer}
                                onOfferClick={onOfferClick}
                            />
                        </Box>
                    ))}
                </Box>

                <IconButton
                    onClick={onNext}
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        position: 'absolute',
                        right: -60,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 20,
                        backgroundColor: 'white',
                        width: 48,
                        height: 48
                    }}
                >
                    <ChevronRight fontSize='large' />
                </IconButton>
            </Box>
        </Container>
    )
}

// Banner Card Component - Responsive for both mobile and desktop
const BannerCard: React.FC<{ offer: FlightOffer; onOfferClick: (slug: string | undefined) => void }> = ({ offer, onOfferClick }) => {
    return (
        <Box
            onClick={() => onOfferClick(offer.slug)}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'white',
            }}
        >
            {/* Banner Image */}
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '16/9',
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: '#f5f5f5',
                    flexShrink: 0
                }}
            >
                <Box
                    component="img"
                    src={offer.imageUrl}
                    alt={offer.title || "Offer banner"}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'fill',
                        objectPosition: 'center',
                        display: 'block'
                    }}
                />
            </Box>

            {/* Banner Content */}
            <Box
                sx={{
                    backgroundColor: 'white',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    flexShrink: 0
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: '13px', md: '14px' },
                        color: 'text.primary',
                        textAlign: 'left',
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 400
                    }}
                >
                    {offer.description || ''}
                </Typography>
            </Box>
        </Box>
    )
}

export default HomepageCarousel
export type { HomepageCarouselProps }
